import { useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'

import { usePageStatus } from 'modules/layout/components/ResponsivePage/FactorialOnePage/usePageStatus'
import { useLocalAudioRecording } from 'modules/meetings/lib/hooks/useLocalAudioRecording'
import { useRoomContext } from 'modules/meetings/lib/hooks/useRoomContext'
import { useTranscriptChannel } from 'modules/meetings/lib/hooks/useTranscriptChannel'
import {
  getTranscriptStatus,
  getTranscriptStatusTooltip,
  getTranscriptStatusVariant,
} from 'modules/meetings/lib/utils/transcript'

export const Transcript = () => {
  const { id: meetingId } = useParams<{ id: string }>()
  const { room } = useRoomContext()
  const subscriptionRef = useRef<{
    perform?: (action: string, data: Record<string, unknown>) => void
    unsubscribe?: () => void
  } | null>(null)

  // Only set up the transcript channel if we have the required data
  const roomName = room?.roomName

  // Set up the transcript channel
  const { isConnected, connectionError: connectionError } = useTranscriptChannel(
    Number(meetingId ?? 0), // Provide fallback, though the hook will handle missing values gracefully
    roomName ?? '',
    subscriptionRef
  )

  // Handle audio chunks
  const handleAudioChunk = useCallback(
    async (blob: Blob) => {
      if (!isConnected || !subscriptionRef.current?.perform || !meetingId || !roomName) {
        return
      }

      // Convert blob to base64 for transmission
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64data = reader.result?.toString().split(',')[1]

        if (base64data) {
          subscriptionRef.current?.perform?.('send_audio_chunk', {
            audio_data: base64data,
            mime_type: blob.type,
            size: blob.size,
            timestamp: Date.now(),
          })
        }
      }
      reader.readAsDataURL(blob)
    },
    [isConnected, meetingId, roomName]
  )

  // Use the audio recording hook
  const { isRecording: _isRecording, participantIdentity: _participantIdentity } =
    useLocalAudioRecording({
      onAudioChunk: handleAudioChunk,
      timeslice: 100, // Send chunks every 100ms
    })

  const { setPageStatus } = usePageStatus()
  setPageStatus({
    text: getTranscriptStatus({ isConnected, connectionError }),
    variant: getTranscriptStatusVariant({ isConnected, connectionError }),
    tooltip: getTranscriptStatusTooltip({ isConnected, connectionError }),
  })

  // Don't render anything if we don't have the required room data
  if (!meetingId || !roomName) {
    return null
  }

  return null
}
